/**
 * Homeowner view-only sharing — client service.
 *
 * Mirrors `web-app/src/services/recordingShareService.js`. The four owner /
 * public callables wrap the `homeowner-sharing.js` Cloud Functions; the
 * `subscribeStructure` LIVE seam delegates to the existing structure
 * subscription in `structureService`. See
 * `claude-docs/2026-06-08-homeowner-viewing-contracts.md`.
 */
import { createCallable } from '../../../firebaseFunctions';
import { subscribeStructureWithRooms } from '../../../services/structureService';

const createHomeownerShareFn = createCallable('createHomeownerShare');
const resolveHomeownerShareFn = createCallable('resolveHomeownerShare');
const revokeHomeownerShareFn = createCallable('revokeHomeownerShare');
const listHomeownerSharesFn = createCallable('listHomeownerShares');

export const homeownerShareService = {
  /**
   * Owner: mint (or reuse) a homeowner share link for a structure, marking
   * which rooms are being remodeled.
   * @param {string} projectId
   * @param {string} structureId      Contractor-picked structure to share.
   * @param {string[]} remodelRoomIds Marked rooms (the homeowner's switcher set).
   * @returns {Promise<{ shareId: string, token: string }>}
   */
  async createShare(projectId, structureId, remodelRoomIds) {
    const res = await createHomeownerShareFn({
      projectId,
      structureId,
      remodelRoomIds,
    });
    return res.data;
  },

  /**
   * Public: resolve a share token into the viewer bootstrap payload:
   * `{ projectId, structureId, projectName, remodelRoomIds, enabled }`. The
   * viewer then live-subscribes to the structure via `subscribeStructure`.
   *
   * Call it signed in (`authService.ensureShareSession`): the callable then
   * grants that uid read access to the structure, without which the
   * subscription is refused.
   */
  async resolveShare(token) {
    const res = await resolveHomeownerShareFn({ token });
    return res.data;
  },

  /** Owner: revoke a homeowner share link. */
  async revokeShare(shareId) {
    const res = await revokeHomeownerShareFn({ shareId });
    return res.data;
  },

  /** Owner: list a project's homeowner share links (for the manage UI). */
  async listShares(projectId) {
    const res = await listHomeownerSharesFn({ projectId });
    return res.data.shares;
  },

  /**
   * LIVE read seam. Subscribe to a structure's data so the homeowner sees the
   * contractor's ongoing edits. Returns an unsubscribe function.
   *
   * Delegates to `structureService.subscribeStructureWithRooms` (an
   * `onSnapshot` over the structure doc + its rooms, assembled into the viewer
   * shape). A visitor with no project role reads it through a share grant
   * (see `resolveShare`).
   *
   * @param {string} structureId
   * @param {(data: object) => void} onData
   * @param {(error: Error) => void} [onError]
   * @returns {() => void} unsubscribe
   */
  subscribeStructure(structureId, onData, onError) {
    return subscribeStructureWithRooms(structureId, onData, onError);
  },
};
